import { memo, ReactElement, useCallback, useMemo } from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { Button, Flex, Select } from 'antd';
import { useTranslation } from 'react-i18next';

import { Table, TableColumns, ValueLists } from '@ohpen/enso-ui';
import { AssistantPermissionType, FieldType } from '@pmp/api';

import { AssistantsListProps, CollaborationRow } from '../interfaces';
import { assistantsColumns } from '../tableConfig';

const DEFAULT_COLLABORATION = AssistantPermissionType.VIEW;

export const AssistantsList = memo(
  ({ readonly, availableAssistants, currentIntermediaryId, collaborationRows, onCollaborationRowsChange, roleOptions, collaborationOptions }: AssistantsListProps): ReactElement => {
    const { t } = useTranslation();

    const valueLists: ValueLists = useMemo(
      () => ({ [FieldType.USER_ROLE_TYPE]: Object.fromEntries((roleOptions ?? []).map(o => [o.value, o.label])) }),
      [roleOptions],
    );

    const assistantsById = useMemo(
      () => new Map((availableAssistants ?? []).map(a => [a.id, a])),
      [availableAssistants],
    );

    const dataSource = useMemo(
      () =>
        (collaborationRows ?? []).map(row => {
          const assistant = assistantsById.get(row.assistantId);
          return {
            ...row,
            name: row.name || assistant?.displayName || row.assistantId,
            roles: row.roles ?? assistant?.roles,
            email: row.email ?? assistant?.email,
          };
        }),
      [collaborationRows, assistantsById],
    );

    const handleSelectAssistants = useCallback(
      (ids: string[]) => {
        const assistantMap = new Map((availableAssistants ?? []).map(a => [a.id, a]));
        const kept = (collaborationRows ?? []).filter(r => ids.includes(r.assistantId));
        const existingIds = new Set((collaborationRows ?? []).map(r => r.assistantId));
        const added = ids
          .filter(id => !existingIds.has(id))
          .map(id => {
            const assistant = assistantMap.get(id);
            return {
              assistantId: id,
              name: assistant?.displayName ?? id,
              collaborationType: DEFAULT_COLLABORATION,
              roles: assistant?.roles,
              email: assistant?.email,
            };
          });
        onCollaborationRowsChange?.([...kept, ...added]);
      },
      [availableAssistants, collaborationRows, onCollaborationRowsChange],
    );

    const handleCollaborationChange = useCallback(
      (assistantId: string, value: string) => {
        onCollaborationRowsChange?.(
          (collaborationRows ?? []).map(r => (r.assistantId === assistantId ? { ...r, collaborationType: value } : r)),
        );
      },
      [collaborationRows, onCollaborationRowsChange],
    );

    const handleRemoveRow = useCallback(
      (assistantId: string) => {
        onCollaborationRowsChange?.((collaborationRows ?? []).filter(r => r.assistantId !== assistantId));
      },
      [collaborationRows, onCollaborationRowsChange],
    );

    const columns: TableColumns<CollaborationRow> = useMemo(
      () =>
        assistantsColumns.map(column =>
          column.key === 'collaborationType'
            ? {
              ...column,
              render: (_: unknown, record: CollaborationRow) => (
                <Select
                  value={record.collaborationType}
                  options={collaborationOptions}
                  onChange={value => handleCollaborationChange(record.assistantId, value)}
                  style={{ width: 220 }}
                  disabled={readonly}
                />
              ),
            }
            : column,
        ),
      [collaborationOptions, handleCollaborationChange, readonly],
    );

    const getRowActions = useCallback(
      (record: CollaborationRow): (ReactElement | null)[] => [
        <Button
          key="remove"
          type="text"
          danger
          aria-label="Remove assistant"
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveRow(record.assistantId)}
          disabled={readonly}
        />,
      ],
      [handleRemoveRow, readonly],
    );

    const assistantOptions = useMemo(
      () =>
        (availableAssistants ?? [])
          .filter(a => a.id !== currentIntermediaryId)
          .map(a => ({ label: a.displayName, value: a.id })),
      [availableAssistants, currentIntermediaryId],
    );

    const selectedIds = (collaborationRows ?? []).map(r => r.assistantId);
    return (
      <Flex vertical gap={16}>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder={t('lender.brokers.collaborations.selectAssistants', 'Select assistants')}
          value={selectedIds}
          onChange={handleSelectAssistants}
          options={assistantOptions}
          disabled={readonly}
        />
        <Table
          id="assistants-collaborations"
          dataSource={dataSource}
          columns={columns}
          rowKey="assistantId"
          valueLists={valueLists}
          getRowActions={getRowActions}
          pagination={false}
          manageColumns={false}
          showViews={false}
          fillHeight={false}
          locale={{ emptyText: t('lender.brokers.collaborations.noAssistants', 'No assistants selected') }}
        />
      </Flex>
    );
  },
);
